// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Checkout / online payment  (Razorpay Checkout wrapper)
// Flow: (1) ask OUR backend to create a Razorpay order for the amount (utils/payments.ts) → (2) open Razorpay's hosted
// checkout for that order → (3) on success send the returned {order id, payment id, signature} to our backend, which
// checks the HMAC with the key secret. `verified` is true only when that check passes; if the check itself can't be
// completed (network) the payment is still reported as a success with verified:false — the customer may have been
// charged, so it must never be shown as a failure. The key id comes from the backend, so nothing is configured in the
// frontend. Razorpay's <script> is loaded only when the customer pays.
// ─────────────────────────────────────────────────────────────────────────────
import { ApiError } from "./api";
import { createPaymentOrder, verifyPayment } from "./payments";

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description: string;
  prefill?: { name?: string; email?: string; contact?: string };
  handler: (response: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: "payment.failed", handler: (response: { error: { description: string } }) => void) => void;
}

let scriptPromise: Promise<boolean> | null = null;

/** Idempotent <script> injection, cached so a second call while the first is still loading reuses the same promise. */
function loadRazorpayScript(): Promise<boolean> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(false);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      scriptPromise = null; // let a later attempt retry instead of caching the failure
      resolve(false);
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

export interface CheckoutOutcome {
  outcome: "success" | "failed" | "cancelled" | "unconfigured" | "load_error" | "refused";
  paymentId?: string;
  /** success only: Razorpay's order id (the backend checked its signature). */
  gatewayOrderId?: string;
  /** success only: the backend confirmed Razorpay's signature. */
  verified?: boolean;
  /** true when the payment ran in Razorpay TEST mode (no real money). */
  testMode?: boolean;
  reason?: string;
}

export interface OpenCheckoutOptions {
  /** Whole rupees. */
  amountInr: number;
  /** Our order reference; stored on the Razorpay order. */
  receipt: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  /** The delivery pin and basket subtotal, checked against the delivery zones before the payment starts. */
  delivery?: { lat: number; lng: number; subtotal: number };
}

export async function openRazorpayCheckout(options: OpenCheckoutOptions): Promise<CheckoutOutcome> {
  let order;
  try {
    order = await createPaymentOrder(options.amountInr, options.receipt, options.delivery);
  } catch (error) {
    if (error instanceof ApiError && (error.code === "NOT_SERVICEABLE" || error.code === "BELOW_MINIMUM_ORDER" || error.code === "PAYMENTS_PAUSED" || error.code === "STOREFRONT_PAUSED")) return { outcome: "refused", reason: error.message };
    if (error instanceof ApiError && error.code === "PAYMENTS_NOT_CONFIGURED") return { outcome: "unconfigured" };
    return { outcome: "load_error" };
  }

  const loaded = await loadRazorpayScript();
  if (!loaded || !window.Razorpay) return { outcome: "load_error" };

  return new Promise((resolve) => {
    let settled = false;
    const razorpay = new window.Razorpay!({
      key: order.keyId,
      amount: order.amountPaise,
      currency: order.currency,
      order_id: order.orderId,
      name: "ZenFresh",
      description: "Fresh seafood order",
      prefill: { name: options.customerName, email: options.customerEmail, contact: options.customerPhone },
      handler: (response) => {
        settled = true;
        void verifyPayment({ orderId: response.razorpay_order_id, paymentId: response.razorpay_payment_id, signature: response.razorpay_signature }).then(
          ({ verified }) => resolve({ outcome: "success", paymentId: response.razorpay_payment_id, gatewayOrderId: order.orderId, verified, testMode: !order.livemode }),
          () => resolve({ outcome: "success", paymentId: response.razorpay_payment_id, gatewayOrderId: order.orderId, verified: false, testMode: !order.livemode }),
        );
      },
      modal: {
        ondismiss: () => {
          if (!settled) resolve({ outcome: "cancelled" });
        },
      },
    });
    razorpay.on("payment.failed", (response) => {
      settled = true;
      resolve({ outcome: "failed", reason: response.error.description });
    });
    razorpay.open();
  });
}
